import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  ProgressBar,
  Spinner,
  Text,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  FolderOpen16Filled,
  FolderOpen16Regular,
} from '@fluentui/react-icons';
import { useCallback, useRef, useState } from 'react';

import { useStyles } from '../DatasetAnnotationsTab.style.js';

import type { UploadProgress } from './annotationUploadUtils.js';

const FolderIcon = bundleIcon(
  FolderOpen16Filled,
  FolderOpen16Regular,
) as React.FC;

export interface UploadAnnotationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (files: FileList, annotationSetName: string) => void;
  isUploading: boolean;
  uploadProgress: UploadProgress | null;
  uploadError: string | null;
}

export function UploadAnnotationDialog({
  open,
  onOpenChange,
  onConfirm,
  isUploading,
  uploadProgress,
  uploadError,
}: UploadAnnotationDialogProps) {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [annotationSetName, setAnnotationSetName] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [folderLabel, setFolderLabel] = useState('');

  const resetForm = useCallback(() => {
    setAnnotationSetName('');
    setSelectedFiles(null);
    setFolderLabel('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFolderPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        setSelectedFiles(files);
        const folderPath = files[0]!.webkitRelativePath;
        const folderName = folderPath.split('/')[0] ?? '';
        setFolderLabel(
          `${folderName} (${files.length} file${files.length === 1 ? '' : 's'})`,
        );
      }
    },
    [],
  );

  const canConfirm =
    selectedFiles !== null &&
    selectedFiles.length > 0 &&
    annotationSetName.trim().length > 0;

  const handleConfirm = useCallback(() => {
    if (!canConfirm || selectedFiles === null) return;
    onConfirm(selectedFiles, annotationSetName.trim());
  }, [canConfirm, selectedFiles, annotationSetName, onConfirm]);

  const progressValue =
    uploadProgress !== null && uploadProgress.totalFiles > 0
      ? (uploadProgress.completedFiles + uploadProgress.failedFiles) /
        uploadProgress.totalFiles
      : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (isUploading) return;
        onOpenChange(data.open);
        if (!data.open) resetForm();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Upload Annotation Data</DialogTitle>
          <DialogContent>
            <div className={styles.dialogForm}>
              <Field label="Annotation set name" required>
                <Input
                  value={annotationSetName}
                  onChange={(_e, d) => setAnnotationSetName(d.value)}
                  placeholder="e.g. my-annotation-set"
                  disabled={isUploading}
                />
              </Field>
              <Field label="Source folder" required>
                <div className={styles.filePickerRow}>
                  <Input
                    readOnly
                    value={folderLabel}
                    placeholder="Select or type a folder path"
                    className={styles.filePickerInput}
                    disabled={isUploading}
                  />
                  <Button
                    icon={<FolderIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                  >
                    Browse
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    {...({ webkitdirectory: '', directory: '' } as Record<
                      string,
                      string
                    >)}
                    className={styles.hiddenInput}
                    onChange={handleFolderPick}
                  />
                </div>
              </Field>
              {isUploading && uploadProgress !== null && (
                <div className={styles.progressSection}>
                  <ProgressBar
                    value={progressValue}
                    max={1}
                    shape="rounded"
                    thickness="large"
                  />
                  <Text size={200} className={styles.progressText}>
                    {uploadProgress.phase === 'preparing' &&
                      'Preparing upload…'}
                    {uploadProgress.phase === 'uploading' &&
                      `Uploaded ${uploadProgress.completedFiles} of ${uploadProgress.totalFiles} files` +
                        (uploadProgress.failedFiles > 0
                          ? ` (${uploadProgress.failedFiles} failed)`
                          : '') +
                        (uploadProgress.currentSubfolder
                          ? ` — ${uploadProgress.currentSubfolder}`
                          : '')}
                    {uploadProgress.phase === 'done' &&
                      `Done: ${uploadProgress.completedFiles} uploaded` +
                        (uploadProgress.failedFiles > 0
                          ? `, ${uploadProgress.failedFiles} failed`
                          : '')}
                  </Text>
                </div>
              )}
              {uploadError !== null && (
                <Text
                  size={200}
                  className={mergeClasses(
                    styles.errorState,
                    styles.preWrapText,
                  )}
                >
                  {uploadError}
                </Text>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isUploading}
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canConfirm || isUploading}
              icon={isUploading ? <Spinner size="tiny" /> : undefined}
              onClick={handleConfirm}
            >
              {isUploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
